import { memo, useEffect, useRef, useState } from "react";
import { Minus, Plus, Trash2, Cable, ShoppingCart, Search } from "lucide-react";
import { TRY, NUM } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IMG } from "@/components/pos/PosBits";
import { useIsDesktop } from "@/hooks/useMediaQuery";

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
// Canlı hazırlık listesindeki durum (depo): hazır ✓ / verilen-istenen / değişiklik depoda görülmedi
const PrepChip = ({ p, pid, fresh }) => {
  if (!p) return null;
  if (fresh && p.prepared) return <span className="ge-ready-pop inline-flex items-center rounded-full bg-emerald-600 text-white px-1.5 py-px text-[9.5px] font-black tracking-wide shadow-sm shadow-emerald-500/40" data-testid={`qs-prep-ready-${pid}`}>HAZIR ✓</span>;
  const cls = p.change ? "bg-orange-500/15 text-orange-700 dark:text-orange-300" : p.prepared ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : p.out_of_stock ? "bg-red-500/15 text-red-700" : "bg-sky-500/15 text-sky-700 dark:text-sky-300";
  const txt = p.change ? "Depo değişikliği görmedi" : p.out_of_stock ? "Depo: stokta yok" : p.prepared ? "Depo: hazır ✓" : `Depo: ${NUM(p.delivered)}/${NUM(p.quantity)}`;
  return <span className={cn("rounded px-1 py-px text-[9px] font-bold tracking-wide", cls)} data-testid={`qs-prep-chip-${pid}`}>{txt}</span>;
};
// Sütun ızgarası: # · görsel · ürün · adet · isk% · birim ₺ · tutar · sil  (kısıtlı: # · görsel · ürün · adet · sil)
export const GRID_FULL = "grid-cols-[28px_40px_minmax(0,1fr)_108px_64px_104px_108px_36px]";
export const GRID_RESTRICTED = "grid-cols-[28px_40px_minmax(0,1fr)_108px_36px]";
const cellIn = "h-9 tnum font-bold text-[13px] rounded-lg bg-card border-border focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0";

// Yeni eklenen satır kısa süre yeşil vurgulanır
const useAddFlash = () => {
  const [flash, setFlash] = useState(true);
  const first = useRef(true);
  useEffect(() => { if (first.current) { first.current = false; const t = setTimeout(() => setFlash(false), 700); return () => clearTimeout(t); } }, []);
  return flash;
};

export function CartTableHead({ restricted }) {
  return (
    <div className={cn("grid items-center gap-x-2 px-3 h-8 text-[10px] font-extrabold uppercase tracking-[0.14em] text-muted-foreground border-b border-border bg-muted/50 sticky top-0 z-10", restricted ? GRID_RESTRICTED : GRID_FULL)} data-testid="qs-cart-head">
      <span className="text-center">#</span><span />
      <span>Ürün</span>
      <span className="text-center">Adet</span>
      {!restricted && <><span className="text-center">İsk %</span><span className="text-right">Birim ₺</span><span className="text-right">Tutar</span></>}
      <span />
    </div>
  );
}

const Stepper = ({ pid, qty, onQty, onInc, tall }) => (
  <div className={cn("inline-flex shrink-0 items-center rounded-lg border border-border bg-card overflow-hidden", tall ? "h-10" : "h-9")}>
    <button type="button" className={cn("h-full grid place-items-center text-muted-foreground hover:bg-muted hover:text-foreground active:scale-95 transition-all", tall ? "w-9" : "w-8")} onClick={() => onInc(pid, -1)} data-testid={`qs-dec-${pid}`} aria-label="Azalt"><Minus className="h-3.5 w-3.5" /></button>
    <Input className={cn("flex-1 min-w-0 h-full text-center tnum font-bold text-[13px] bg-transparent border-0 shadow-none focus-visible:ring-0 px-0 rounded-none", tall ? "w-11" : "w-10")} type="number" value={qty} onChange={(e) => onQty(pid, e.target.value)} data-testid={`qs-qty-${pid}`} />
    <button type="button" className={cn("h-full grid place-items-center text-muted-foreground hover:bg-muted hover:text-foreground active:scale-95 transition-all", tall ? "w-9" : "w-8")} onClick={() => onInc(pid, 1)} data-testid={`qs-inc-${pid}`} aria-label="Artır"><Plus className="h-3.5 w-3.5" /></button>
  </div>
);

const DiscInput = ({ pid, l, onDisc, tall }) => {
  const lp = Number(l.list_price) || 0;
  return (
    <div className="relative">
      <Input className={cn(cellIn, tall && "h-10", "text-center pl-1 pr-4 disabled:opacity-40")} type="number" min="0" max="100" value={l.sale_discount} onChange={(e) => onDisc(pid, e.target.value)} disabled={l.manual_price || lp <= 0}
        title={l.manual_price ? "Manuel fiyat uygulanıyor — iskonto devre dışı" : (lp > 0 ? "İskonto %" : "Liste fiyatı yok — birim fiyatı manuel girin")} data-testid={`qs-disc-${pid}`} />
      <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-muted-foreground pointer-events-none">%</span>
    </div>
  );
};

const GrossInput = ({ pid, l, onGross, tall }) => (
  <div className="relative">
    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-muted-foreground pointer-events-none">₺</span>
    <Input className={cn(cellIn, tall && "h-10", "text-right pl-5 pr-2 bg-primary/[0.06] border-primary/20 focus-visible:bg-card")} type="number" min="0" step="0.01" value={l.gross} onChange={(e) => onGross(pid, e.target.value)} title="KDV dahil birim satış fiyatı — elle yazılabilir" data-testid={`qs-gross-${pid}`} />
  </div>
);

const DrumSelect = ({ pid, l, onDrum, className }) => (
  <div className={cn("flex items-center gap-2", className)} data-testid={`qs-drum-wrap-${pid}`}>
    <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1 shrink-0"><Cable className="h-3 w-3" />Makara</span>
    <Select value={l.drum_id || "__none"} onValueChange={(v) => onDrum(pid, v === "__none" ? "" : v)}>
      <SelectTrigger className="h-7 text-[11px] max-w-xs rounded-lg" data-testid={`qs-drum-${pid}`}><SelectValue placeholder="Makara seç" /></SelectTrigger>
      <SelectContent><SelectItem value="__none">Belirtme</SelectItem>{l.drums.map((d) => <SelectItem key={d.id} value={d.id}>{d.label} · {NUM(d.remaining)} {l.unit} kaldı</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

// Yoğun sepet satırı (≈48 px, masaüstü): 10–30 kalem tek bakışta
export const CartRow = memo(function CartRow({ l, idx, unitNet, lineGross, restricted, onQty, onInc, onDisc, onGross, onDrum, onRemove, prep, fresh }) {
  const pid = l.product_id;
  const out = (Number(l.stock) || 0) <= 0;
  const flash = useAddFlash();
  return (
    <div className={cn("group border-b border-border/70 transition-colors", flash ? "bg-emerald-500/10" : idx % 2 ? "bg-muted/25" : "bg-card", "hover:bg-primary/[0.04]", fresh && "ge-ready-row")} data-testid={`qs-cart-${pid}`}>
      <div className={cn("grid items-center gap-x-2 px-3 min-h-[48px] py-1", restricted ? GRID_RESTRICTED : GRID_FULL)}>
        <span className="text-center text-[11px] font-mono text-muted-foreground tnum">{idx + 1}</span>
        <IMG src={l.image_url} alt={l.name} className="h-9 w-9" />
        <div className="min-w-0 leading-tight">
          <div className="font-semibold text-[13px] truncate" title={l.name}>{l.name}</div>
          <div className="mt-0.5 text-[10.5px] text-muted-foreground flex items-center gap-x-1.5 flex-wrap tnum leading-none">
            {l.code && <span className="font-mono">{l.code}</span>}
            <span>Stok <b className={cn(out ? "text-red-500" : "text-foreground/80")}>{NUM(l.stock)}</b></span>
            {!restricted && <>
              <span>KDV %{r2(l.kdv_rate)}</span>
              <span className="hidden xl:inline">Net <b className="text-foreground/80" data-testid={`qs-unit-net-${pid}`}>{TRY(unitNet)}</b>/{(l.unit || "ADET").toLowerCase()}</span>
              {l.manual_price && <span className="rounded bg-primary/10 text-primary px-1 py-px text-[9px] font-bold tracking-wide" data-testid={`qs-manual-badge-${pid}`}>MANUEL</span>}
            </>}
            <PrepChip p={prep} pid={pid} fresh={fresh} />
          </div>
        </div>
        <Stepper pid={pid} qty={l.quantity} onQty={onQty} onInc={onInc} />
        {!restricted && <>
          <DiscInput pid={pid} l={l} onDisc={onDisc} />
          <GrossInput pid={pid} l={l} onGross={onGross} />
          <div className="text-right font-head font-black tnum text-[14.5px] text-foreground" data-testid={`qs-line-total-${pid}`}>{TRY(lineGross)}</div>
        </>}
        <button type="button" className="h-9 w-9 grid place-items-center rounded-lg text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10 active:scale-95 transition-all" onClick={() => onRemove(pid)} title="Sepetten çıkar" data-testid={`qs-rm-${pid}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      {l.is_cable && l.drums && l.drums.length > 0 && <DrumSelect pid={pid} l={l} onDrum={onDrum} className="px-3 pb-2 pl-[88px]" />}
    </div>
  );
});

// Mobil sepet kartı: ad tam görünür (2 satır), altında adet / iskonto / birim fiyat dokunmatik kontroller
export const CartRowMobile = memo(function CartRowMobile({ l, idx, unitNet, lineGross, restricted, onQty, onInc, onDisc, onGross, onDrum, onRemove, prep, fresh }) {
  const pid = l.product_id;
  const out = (Number(l.stock) || 0) <= 0;
  const flash = useAddFlash();
  return (
    <div className={cn("border-b border-border/70 px-3 py-2.5 transition-colors", flash ? "bg-emerald-500/10" : "bg-card", fresh && "ge-ready-row")} data-testid={`qs-cart-${pid}`}>
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 w-5 shrink-0 text-center text-[10px] font-mono text-muted-foreground tnum">{idx + 1}</span>
        <IMG src={l.image_url} alt={l.name} className="h-10 w-10" />
        <div className="flex-1 min-w-0 leading-snug">
          <div className="font-semibold text-[13px] break-words line-clamp-2" title={l.name}>{l.name}</div>
          <div className="mt-0.5 text-[10.5px] text-muted-foreground flex items-center gap-x-1.5 flex-wrap tnum leading-tight">
            {l.code && <span className="font-mono">{l.code}</span>}
            <span>Stok <b className={cn(out ? "text-red-500" : "text-foreground/80")}>{NUM(l.stock)}</b></span>
            {!restricted && <>
              <span>KDV %{r2(l.kdv_rate)}</span>
              <span>Net <b className="text-foreground/80" data-testid={`qs-unit-net-${pid}`}>{TRY(unitNet)}</b>/{(l.unit || "ADET").toLowerCase()}</span>
              {l.manual_price && <span className="rounded bg-primary/10 text-primary px-1 py-px text-[9px] font-bold tracking-wide" data-testid={`qs-manual-badge-${pid}`}>MANUEL</span>}
            </>}
            <PrepChip p={prep} pid={pid} fresh={fresh} />
          </div>
        </div>
        {!restricted && <div className="shrink-0 text-right font-head font-black tnum text-[15px] text-foreground leading-tight" data-testid={`qs-line-total-${pid}`}>{TRY(lineGross)}</div>}
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        <Stepper pid={pid} qty={l.quantity} onQty={onQty} onInc={onInc} tall />
        {!restricted && <>
          <div className="w-[62px] shrink-0"><DiscInput pid={pid} l={l} onDisc={onDisc} tall /></div>
          <div className="flex-1 min-w-[96px]"><GrossInput pid={pid} l={l} onGross={onGross} tall /></div>
        </>}
        <button type="button" className={cn("h-10 w-9 shrink-0 grid place-items-center rounded-lg text-muted-foreground/70 hover:text-destructive hover:bg-destructive/10 active:scale-95 transition-all", restricted && "ml-auto")} onClick={() => onRemove(pid)} title="Sepetten çıkar" data-testid={`qs-rm-${pid}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      {l.is_cable && l.drums && l.drums.length > 0 && <DrumSelect pid={pid} l={l} onDrum={onDrum} className="mt-2" />}
    </div>
  );
});

export function CartTable({ cart, restricted, unitNet, lineGross, handlers, onGoSearch, prepItems, readyKeys }) {
  const isDesktop = useIsDesktop();
  const Row = isDesktop ? CartRow : CartRowMobile;
  const prepBy = {};
  (prepItems || []).forEach((p) => { if (p.key && !p.removed) prepBy[p.key] = p; });
  return (
    <div className="flex-1 min-h-0 flex flex-col rounded-xl border border-border bg-card overflow-hidden" data-testid="qs-cart-list">
      {cart.length > 0 && isDesktop && <CartTableHead restricted={restricted} />}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" data-testid="qs-cart-scroll">
        {cart.length === 0 ? (
          <div className="h-full min-h-[220px] flex flex-col items-center justify-center text-center text-sm text-muted-foreground p-8">
            <div className="mb-3 h-14 w-14 rounded-2xl bg-muted text-muted-foreground/70 grid place-items-center"><ShoppingCart className="h-6 w-6" /></div>
            <p className="font-head font-bold text-foreground/70 text-base">Sepet boş</p>
            <p className="text-xs mt-1">{onGoSearch ? "Ürün arayıp ekleyin ya da barkod okutun." : "Soldan ürün arayıp ekleyin ya da barkod okutun."}</p>
            {onGoSearch && <button type="button" onClick={onGoSearch} className="mt-4 inline-flex h-10 items-center gap-1.5 rounded-xl bg-primary px-4 text-sm font-bold text-white shadow-md shadow-primary/25 active:scale-[0.98] transition-transform" data-testid="qs-cart-empty-search-btn"><Search className="h-4 w-4" />Ürün Ara</button>}
          </div>
        ) : cart.map((l, i) => (
          <Row key={l.product_id} l={l} idx={i} unitNet={unitNet(l)} lineGross={lineGross(l)} restricted={restricted} prep={prepBy[l.product_id]} fresh={!!readyKeys?.has(l.product_id)}
            onQty={handlers.setQtyLine} onInc={handlers.incQty} onDisc={handlers.setDiscLine} onGross={handlers.setGrossLine} onDrum={handlers.setDrumLine} onRemove={handlers.rmLine} />
        ))}
      </div>
    </div>
  );
}
